import firebase from "firebase";
import React, { useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { AmplitudeUserProperties } from "@lifeat/shared/amplitude/AmplitudeUserProperties";
import { QueryParameters, Routes } from "@lifeat/shared/system/RouteConstants";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { getAppVersion, hideMenubarWindow, reloadMenubarWindow, showMainWindow } from "@desktop/ToDesktopService";
import { auth } from "@firebase-local/firebase";
import useQueryParam from "@hooks/useQueryParam";
import { BrowserUtil } from "@util/index";
import LoadingRedirectPage from "../../../../app/Pages/LoadingRedirectPage";
import { UseToDesktopBuilderAppEvent } from "../../AmplitudeDesktopAppEvents";
import DesktopAppMainLanding from "./DesktopAppMainLanding";

// This is doing a redirect as soon as the desktop app opens to take the user to the
// true room access page. Cuz every deep link gets prefixed with the app's base
// route (`/desktop/main`). So if we see this route + `/room-access` it redirects
// to the root route of the entire app + `/room-access`, which is what we have
// happening in the webapp as well
if (location.pathname.includes(`/desktop/main/room-access`)) {
    BrowserUtil.redirectTo(location.pathname.split("/desktop/main")[1]);
}

// Route: /desktop/main
function DesktopAppMainWindow() {
    const history = useHistory();
    const query = useQueryParam();

    const amplitudeService = AmplitudeService.getInstance();

    const { user, userData }: UserContextType = useUserContext();

    const showMainWindowOnly = async () => {
        await showMainWindow();
        await hideMenubarWindow();
    };

    const isAuth = useRef(null);

    useEffect(() => {
        if (window.todesktop) {
            amplitudeService.logEvent(
                new UseToDesktopBuilderAppEvent({
                    appVersion: getAppVersion(),
                }),
            );
            amplitudeService.setUserPropOnce(AmplitudeUserProperties.HAS_USED_DESKTOP_APP, true);
        }

        const unsubscribe = auth.onAuthStateChanged(async function (stateChangeUser: firebase.User | null) {
            isAuth.current = stateChangeUser && stateChangeUser.emailVerified;
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (user && user.emailVerified && (window.todesktop || window.location.origin.includes("localhost"))) {
            isAuth.current = true;

            // When the user verifies their email for the first time, we need
            // to reload the menubar so the correct elements render.
            if (user?.emailVerified) {
                reloadMenubarWindow();
            }
        } else {
            // If the user was unauthenticated, we only show the main window so they can follow the
            // onboarding. We set the state to indicate that this happened so we only
            // show the menubar window on subsequent launches.
            showMainWindowOnly();
        }
    }, [user, user?.emailVerified, userData?.additionalUserSuppliedData]);

    useEffect(() => {
        // determines where to redirect user to after auth
        if (isAuth.current) {
            if (userData) {
                history.replace(query.get(QueryParameters.RedirectSuccess) || `/${Routes.App}`);
            }
        }
    }, [history, query, userData]);

    return isAuth.current ? <LoadingRedirectPage preventRedirect={true} /> : <DesktopAppMainLanding />;
}
export default DesktopAppMainWindow;
